import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Pin, PinOff, X, Plus, Trash2, Share2 } from "lucide-react";
import { shareLink, pollShareUrl } from "@/lib/share";

/**
 * Polls surface for a room. Sits above the messages list.
 * Registered users + admins can create polls; anyone can vote once.
 * Admins can pin one poll per room to the top.
 */
export default function PollsPanel({ slug, user }) {
  const [polls, setPolls] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${encodeURIComponent(slug)}/polls`);
      setPolls(data.polls || []);
    } catch { /* silent */ }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Refresh live results periodically while the tab is visible.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 8000);
    return () => clearInterval(t);
  }, [load]);

  const vote = async (poll, option_id) => {
    try {
      const { data } = await api.post(`/polls/${poll.id}/vote`, { option_id });
      setPolls((xs) => xs.map((p) => (p.id === data.id ? data : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record vote.");
    }
  };

  const endPoll = async (poll) => {
    if (!window.confirm("End this poll? Nobody will be able to vote anymore.")) return;
    try {
      const { data } = await api.post(`/polls/${poll.id}/end`);
      setPolls((xs) => xs.map((p) => (p.id === data.id ? data : p)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const deletePoll = async (poll) => {
    if (!window.confirm("Delete this poll and all its votes?")) return;
    try {
      await api.delete(`/polls/${poll.id}`);
      setPolls((xs) => xs.filter((p) => p.id !== poll.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const togglePin = async (poll) => {
    try {
      const { data } = await api.post(`/polls/${poll.id}/${poll.is_pinned ? "unpin" : "pin"}`);
      // Refresh the whole list so ordering follows the new pinned state.
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const onCreated = (poll) => {
    setPolls((xs) => [poll, ...xs.filter((p) => p.id !== poll.id)]);
    setShowCreate(false);
  };

  const canCreate = !!user && !user.is_guest;

  if (!polls.length && !canCreate) return null;

  return (
    <section className="border-b border-white/10 px-4 md:px-8 py-3 space-y-3" data-testid="polls-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/50 uppercase">
          <BarChart3 className="w-3 h-3" /> Polls · {polls.length}
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="new-poll-btn"
            className="font-mono text-[10px] px-3 py-1.5 border-2 border-acid/60 text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> New poll
          </button>
        )}
      </div>

      {polls.map((p) => (
        <PollCard
          key={p.id}
          poll={p}
          me={user}
          onVote={(opt) => vote(p, opt)}
          onEnd={() => endPoll(p)}
          onDelete={() => deletePoll(p)}
          onTogglePin={() => togglePin(p)}
        />
      ))}

      {showCreate && (
        <NewPollModal slug={slug} onClose={() => setShowCreate(false)} onCreated={onCreated} />
      )}
    </section>
  );
}

function PollCard({ poll, me, onVote, onEnd, onDelete, onTogglePin }) {
  const isMine = me?.id === poll.created_by;
  const isAdmin = !!me?.is_admin;
  const canManage = isMine || isAdmin;
  const total = poll.total_votes || 0;
  const voted = !!poll.my_vote;
  const closed = !poll.is_active;

  return (
    <div
      className={`border-2 ${poll.is_pinned ? "border-acid" : "border-white/15"} bg-panel/40 p-3 md:p-4`}
      data-testid={`poll-${poll.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] text-white/40 uppercase tracking-widest">
            <span>@{poll.creator_username}</span>
            {poll.is_pinned && <span className="text-acid">// pinned</span>}
            {closed && <span className="text-blaze">// closed</span>}
          </div>
          <div className="font-heading text-lg md:text-xl leading-snug mt-1" data-testid={`poll-question-${poll.id}`}>
            {poll.question}
          </div>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={onTogglePin}
            title={poll.is_pinned ? "Unpin" : "Pin to top"}
            data-testid={`poll-pin-${poll.id}`}
            className={`shrink-0 p-1 border-2 ${poll.is_pinned ? "border-acid text-acid" : "border-white/20 text-white/60 hover:text-acid hover:border-acid"}`}
          >
            {poll.is_pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
          </button>
        )}
        <button
          type="button"
          onClick={async () => {
            const res = await shareLink({
              title: `Poll · ${poll.question} — NITEWIRE`,
              text: `Vote in NITEWIRE: ${poll.question}`,
              url: pollShareUrl(poll.id),
            });
            if (res.method === "copy" && res.ok) toast.success("POLL LINK COPIED");
            else if (res.method === "copy") toast.error("Copy failed.");
          }}
          title="Share poll"
          data-testid={`poll-share-${poll.id}`}
          className="shrink-0 p-1 border-2 border-white/20 text-white/60 hover:text-acid hover:border-acid"
        >
          <Share2 className="w-3 h-3" />
        </button>
        {canManage && !closed && (
          <button
            type="button"
            onClick={onEnd}
            title="End poll"
            data-testid={`poll-end-${poll.id}`}
            className="shrink-0 p-1 border-2 border-white/20 text-white/60 hover:text-blaze hover:border-blaze"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        {canManage && closed && (
          <button
            type="button"
            onClick={onDelete}
            title="Delete poll"
            data-testid={`poll-delete-${poll.id}`}
            className="shrink-0 p-1 border-2 border-white/20 text-white/60 hover:text-blaze hover:border-blaze"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="mt-3 space-y-1.5" data-testid={`poll-options-${poll.id}`}>
        {poll.options.map((opt) => {
          const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
          const isMyPick = poll.my_vote === opt.id;
          const showResults = voted || closed;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => !voted && !closed && onVote(opt.id)}
              disabled={voted || closed}
              data-testid={`poll-option-${opt.id}`}
              className={`relative w-full text-left border-2 ${
                isMyPick ? "border-acid" : "border-white/15"
              } ${!voted && !closed ? "hover:border-acid cursor-pointer" : "cursor-default"} bg-obsidian/40 overflow-hidden`}
            >
              {showResults && (
                <div
                  className={`absolute inset-y-0 left-0 ${isMyPick ? "bg-acid/20" : "bg-white/[0.05]"}`}
                  style={{ width: `${pct}%` }}
                />
              )}
              <div className="relative flex items-center justify-between gap-3 px-3 py-2">
                <span className="font-body text-sm text-white/90 truncate">{opt.text}</span>
                {showResults && (
                  <span className={`font-mono text-[10px] ${isMyPick ? "text-acid" : "text-white/50"} shrink-0`}>
                    {opt.votes} · {pct}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-2 font-mono text-[10px] text-white/40">
        {total} vote{total === 1 ? "" : "s"}{voted && !closed && " · you've voted"}
      </div>
    </div>
  );
}

function NewPollModal({ slug, onClose, onCreated }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [busy, setBusy] = useState(false);

  const setOpt = (i, v) => setOpts((xs) => xs.map((o, idx) => (idx === i ? v : o)));
  const addOpt = () => setOpts((xs) => (xs.length < 4 ? [...xs, ""] : xs));
  const removeOpt = (i) => setOpts((xs) => (xs.length > 2 ? xs.filter((_, idx) => idx !== i) : xs));

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${encodeURIComponent(slug)}/polls`, {
        question: q,
        options: opts,
      });
      onCreated(data);
      toast.success("Poll posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create poll.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-obsidian/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="poll-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-obsidian border-2 border-acid p-6 space-y-4"
      >
        <div>
          <div className="font-mono text-[10px] tracking-widest text-acid">// NEW POLL</div>
          <h2 className="font-heading text-3xl leading-none mt-1">ASK THE ROOM</h2>
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/60">QUESTION</label>
          <input
            data-testid="poll-question-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-brutal mt-1"
            placeholder="e.g. best midnight snack?"
            maxLength={200}
            required
          />
        </div>

        <div className="space-y-2">
          <label className="font-mono text-[10px] tracking-widest text-white/60">OPTIONS — 2 to 4</label>
          {opts.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                data-testid={`poll-option-input-${i}`}
                value={v}
                onChange={(e) => setOpt(i, e.target.value)}
                className="input-brutal flex-1"
                placeholder={`Option ${i + 1}`}
                maxLength={80}
              />
              {opts.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeOpt(i)}
                  className="text-white/40 hover:text-blaze"
                  title="Remove option"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {opts.length < 4 && (
            <button
              type="button"
              onClick={addOpt}
              data-testid="poll-add-option"
              className="font-mono text-[10px] px-3 py-1.5 border-2 border-white/20 text-white/60 hover:text-acid hover:border-acid uppercase tracking-widest flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add option
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="poll-create-submit"
            className="btn-acid flex-1 disabled:opacity-50"
          >
            {busy ? "POSTING…" : "POST POLL"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-xs px-4 py-2 border-2 border-white/20 text-white/70 hover:border-white/40 uppercase tracking-widest"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
